import { typeValidator } from '@chilipiper/utils'
import React, { useState } from 'react'
import invariant from 'tiny-invariant'
import { validatePositioningProps } from '../../../helpers/validatePositioningProps'
import { OmitInternalProperties, PositioningProps } from '../../types'
import { BaseSelect, BaseSelectProps } from '../base-select/BaseSelect'
import { CascadeItems } from '../base-select/CascadeItemSelectList'
import { SelectItemPlaceholder } from '../select-item/SelectItemPlaceholder'
import { DynamicCascadeItem } from '../base-select/dynamicItemUtils'
import { useSelectListData } from '../select-list/useSelectListData'
import {
  CascadeSelectItem,
  SelectEndContent,
  SelectItem,
  SelectLabel,
  SelectSection,
  SelectStartContent,
} from './PublicComponents'

type Props = Omit<
  BaseSelectProps,
  | 'listStateData'
  | 'hasLabelInside'
  | 'setSelectedCascadeItem'
  | 'selectedText'
  | 'selectedCascadeItem'
  | 'hasUserProvidedSearch'
> & {
  children: React.ReactNode
  /** <Select.Item> of current selection, required for paginated data and external search */
  selectedItem?: React.ReactNode
  size?: 'regular' | 'large'
} & PositioningProps

export const SelectInternal = ({
  'data-id': dataId,
  'data-test-id': testId,
  INTERNAL_hideSelectList,
  INTERNAL_isOpen,
  children,
  defaultValue,
  errorMessage,
  hasSearch,
  isDisabled,
  label,
  name,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  value,
  valuePath,
  variant,
  eventsRef,
  startIcon,
  useVirtualScroll,
  isLoading,
  search: propsSearch,
  onSearchChange,
  selectedItem,
  isRequired,
  size = 'regular',
  ...positioningProps
}: Props) => {
  validatePositioningProps(positioningProps, 'Select')
  invariant(typeValidator(SelectLabel)(label), 'Select: label must be a Select.Label component')

  const hasUserProvidedSearch = Boolean(onSearchChange)

  const [searchValue, setSearchValue] = useState('')
  const [selectedCascadeItem, setSelectedCascadeItem] = useState<DynamicCascadeItem[] | undefined>()

  const search = propsSearch ?? searchValue
  const handleSearchChange = onSearchChange ?? setSearchValue

  const listData = useSelectListData({
    children,
    selectedItem,
    // if we have cascade selected the search is only done in that panel, and we don't want to filter inside the component if user provides their own search
    search: selectedCascadeItem || hasUserProvidedSearch ? undefined : search,
  })

  const props = {
    INTERNAL_hideSelectList,
    INTERNAL_isOpen,
    'data-id': dataId,
    'data-test-id': testId,
    defaultValue,
    errorMessage,
    isDisabled,
    name,
    onChange,
    onFocus,
    onBlur,
    selectedCascadeItem,
    setSelectedCascadeItem,
    placeholder,
    value,
    valuePath,
    isRequired,
    variant,
    eventsRef,
    startIcon,
    useVirtualScroll,
    isLoading,
    hasUserProvidedSearch,
  }

  return (
    <BaseSelect
      {...props}
      listStateData={listData}
      label={label}
      onSearchChange={handleSearchChange}
      search={search}
      hasSearch={hasUserProvidedSearch || hasSearch}
      size={size}
      {...positioningProps}
    />
  )
}

type PublicProps = OmitInternalProperties<Props>

export const SelectPublic = (props: PublicProps) => <SelectInternal {...props} />

SelectInternal.Item = SelectItem
SelectInternal.Section = SelectSection
SelectInternal.Label = SelectLabel
SelectInternal.StartContent = SelectStartContent
SelectInternal.EndContent = SelectEndContent
SelectInternal.CascadeItem = CascadeSelectItem
SelectInternal.CascadeItems = CascadeItems
SelectInternal.ItemPlaceholder = SelectItemPlaceholder
SelectPublic.Item = SelectItem
SelectPublic.Section = SelectSection
SelectPublic.Label = SelectLabel
SelectPublic.StartContent = SelectStartContent
SelectPublic.EndContent = SelectEndContent
SelectPublic.CascadeItem = CascadeSelectItem
SelectPublic.CascadeItems = CascadeItems
SelectPublic.ItemPlaceholder = SelectItemPlaceholder
